import type { EntityDto, TreeRelationDto } from "api/types";

import { SATELLITE_BADGE, SATELLITE_HEIGHT, SATELLITE_WIDTH } from "./treeGraph.constants";

/**
 * Entity kinds that carry a tree's structure and always sit on the canvas. Everything else - places,
 * objects, events, species, concepts, custom nodes - waits in a ring around whichever of these the
 * reader selects. Creatures and organizations stay out of the ring for now (TTWEB-34).
 */
export const STRUCTURAL_TYPES = new Set(["Person", "Organization", "Creature"]);

export const isStructural = (entity: Pick<EntityDto, "type">): boolean => STRUCTURAL_TYPES.has(entity.type);

/** A secondary entity waiting in its owner's ring, with the relation that ties it there. */
export type Satellite = {
	entity: EntityDto;
	relation: TreeRelationDto;
};

export type EntitySplit = {
	/** What the canvas lays out and draws: the structural entities plus any orphaned secondary one. */
	canvasEntities: EntityDto[];
	/** Relations whose two endpoints are both on the canvas. */
	canvasRelations: TreeRelationDto[];
	/** Structural entity id → the secondary entities related to it, ordered by title. */
	satellitesByOwner: Map<string, Satellite[]>;
	/** Secondary entity id → the structural entities whose ring it appears in, ordered by title. */
	ownersBySatellite: Map<string, string[]>;
};

const byTitle = (first: string, second: string): number =>
	first.localeCompare(second, undefined, { sensitivity: "base" });

/**
 * Splits a tree into what the canvas draws and what waits in the rings. A secondary entity with no
 * structural neighbour stays on the canvas: otherwise an unlinked place, or one created a moment
 * ago, would be drawn nowhere at all.
 */
export const splitEntities = (entities: EntityDto[], relations: TreeRelationDto[]): EntitySplit => {
	const byId = new Map(entities.map((entity: EntityDto) => [entity.id, entity]));
	// One chip per owner/satellite pair, however many relations join them.
	const pairs = new Map<string, { ownerId: string; satellite: Satellite }>();

	relations.forEach((relation: TreeRelationDto) => {
		const from = byId.get(relation.fromEntityId);
		const to = byId.get(relation.toEntityId);
		if (!from || !to || isStructural(from) === isStructural(to)) {
			return;
		}

		const owner = isStructural(from) ? from : to;
		const secondary = owner === from ? to : from;
		const key = `${owner.id}|${secondary.id}`;
		const existing = pairs.get(key);
		// Of a mirrored pair, keep the row read from the chip's side: "Rivendell is Home Of Elrond".
		if (
			existing === undefined ||
			(existing.satellite.relation.fromEntityId !== secondary.id && from === secondary)
		) {
			pairs.set(key, { ownerId: owner.id, satellite: { entity: secondary, relation } });
		}
	});

	const satellitesByOwner = new Map<string, Satellite[]>();
	const ownersBySatellite = new Map<string, string[]>();
	pairs.forEach(({ ownerId, satellite }: { ownerId: string; satellite: Satellite }) => {
		satellitesByOwner.set(ownerId, [...(satellitesByOwner.get(ownerId) ?? []), satellite]);
		ownersBySatellite.set(satellite.entity.id, [...(ownersBySatellite.get(satellite.entity.id) ?? []), ownerId]);
	});
	satellitesByOwner.forEach((satellites: Satellite[]) =>
		satellites.sort((a: Satellite, b: Satellite) => byTitle(a.entity.title, b.entity.title))
	);
	ownersBySatellite.forEach((owners: string[]) =>
		owners.sort((a: string, b: string) => byTitle(byId.get(a)?.title ?? "", byId.get(b)?.title ?? ""))
	);

	const canvasEntities = entities.filter((entity: EntityDto) => !ownersBySatellite.has(entity.id));
	const canvasRelations = relations.filter(
		(relation: TreeRelationDto) =>
			!ownersBySatellite.has(relation.fromEntityId) && !ownersBySatellite.has(relation.toEntityId)
	);

	return { canvasEntities, canvasRelations, satellitesByOwner, ownersBySatellite };
};

/**
 * Whose ring is open for a selection. A structural entity owns its own ring. A secondary entity
 * (a chip click, or a deep link straight to a place) keeps the ring it was picked from when that
 * owner is related to it, and otherwise opens the ring of its first owner by title. Anything else -
 * nothing selected, or an orphan sitting on the canvas itself - opens no ring.
 */
export const resolveRingOwner = (
	selectedId: string | null,
	previousOwnerId: string | null,
	split: EntitySplit,
	entities: EntityDto[]
): string | null => {
	if (selectedId === null) {
		return null;
	}

	const owners = split.ownersBySatellite.get(selectedId);
	if (owners !== undefined) {
		return previousOwnerId !== null && owners.includes(previousOwnerId) ? previousOwnerId : owners[0];
	}

	const selected = entities.find((entity: EntityDto) => entity.id === selectedId);

	return selected !== undefined && isStructural(selected) ? selectedId : null;
};

/** Chips per ring, innermost first. Past both, the last seat becomes a "+N more" chip. */
export const RING_CAPACITY = [8, 16];
/** Distance from the owner's centre to each ring, in flow units. */
export const RING_RADII = [180, 285];

/** One seat in a ring: the chip centre, and the satellite sitting there (null for "+N more"). */
export type RingSeat = {
	id: string | null;
	x: number;
	y: number;
};

export type RingLayout = {
	seats: RingSeat[];
	/** How many satellites did not fit and are counted by the "+N more" seat (0 when all fit). */
	overflow: number;
};

/** Stable pseudo-random number in [0, 1) from a string, so a chip keeps its jitter across renders. */
const seededUnit = (seed: string): number => {
	const modulus = 2147483647;
	let hash = 7;
	for (let index = 0; index < seed.length; index += 1) {
		hash = (hash * 31 + seed.charCodeAt(index)) % modulus;
	}

	return hash / modulus;
};

/** A box on the canvas a chip must not land on: another node, or a chip already seated. */
export type RingObstacle = {
	x: number;
	y: number;
	width: number;
	height: number;
};

/** Rotations tried per ring, and how far a crowded ring may be pushed out before settling. */
const ROTATION_STEPS = 72;
const RADIUS_SCALES = [1, 1.2, 1.45];
/** Largest organic nudge in angle, so a lone chip is not flung a quarter turn off its seat. */
const MAX_ANGLE_JITTER = 0.2;

/** The box a chip occupies when its badge is centred on a seat. */
const chipBox = (x: number, y: number): RingObstacle => ({
	x: x - SATELLITE_WIDTH / 2,
	y: y - SATELLITE_BADGE / 2,
	width: SATELLITE_WIDTH,
	height: SATELLITE_HEIGHT
});

const overlapArea = (a: RingObstacle, b: RingObstacle): number =>
	Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x)) *
	Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y));

/**
 * Seats satellites on up to two loose rings around a centre point, the way a gesture launcher fans
 * its icons out around the pointer. Each ring is spread evenly, then turned - and pushed a little
 * further out if it has to be - until its chips clear the other nodes on the canvas, so a chip
 * never lands on the owner's parents above or a partner beside. Among arrangements that fit equally
 * well the one starting nearest the top wins, so an uncrowded ring still opens from twelve o'clock.
 * Each chip is also nudged a little in angle and distance, deterministically per entity, so the
 * cluster reads organically rather than as a perfect circle and never shifts between renders.
 */
export const computeRingSeats = (
	center: { x: number; y: number },
	satelliteIds: string[],
	obstacles: RingObstacle[] = []
): RingLayout => {
	const capacity = RING_CAPACITY.reduce((sum: number, count: number) => sum + count, 0);
	const overflow = satelliteIds.length > capacity ? satelliteIds.length - (capacity - 1) : 0;
	const seatedIds: Array<string | null> =
		overflow > 0 ? [...satelliteIds.slice(0, capacity - 1), null] : [...satelliteIds];

	const seats: RingSeat[] = [];
	const taken: RingObstacle[] = [...obstacles];
	let start = 0;
	RING_CAPACITY.forEach((ringCapacity: number, ringIndex: number) => {
		const ringIds = seatedIds.slice(start, start + ringCapacity);
		start += ringCapacity;
		if (ringIds.length === 0) {
			return;
		}

		const step = (2 * Math.PI) / ringIds.length;
		const place = (offset: number, radius: number): RingSeat[] =>
			ringIds.map((id: string | null, index: number) => {
				const seed = id ?? "more";
				const jitter = Math.min(step * 0.15, MAX_ANGLE_JITTER);
				const angle = offset + index * step + (seededUnit(`${seed}:a`) - 0.5) * 2 * jitter;
				const distance = radius + (seededUnit(`${seed}:r`) - 0.5) * 28;

				return { id, x: center.x + Math.cos(angle) * distance, y: center.y + Math.sin(angle) * distance };
			});

		let best: { seats: RingSeat[]; cost: number; lean: number } | null = null;
		for (let scaleIndex = 0; scaleIndex < RADIUS_SCALES.length; scaleIndex += 1) {
			const scale = RADIUS_SCALES[scaleIndex];
			for (let turn = 0; turn < ROTATION_STEPS; turn += 1) {
				const offset = -Math.PI / 2 + (turn * 2 * Math.PI) / ROTATION_STEPS;
				const candidate = place(offset, RING_RADII[ringIndex] * scale);
				const cost = candidate.reduce(
					(sum: number, seat: RingSeat) =>
						sum +
						taken.reduce(
							(area: number, box: RingObstacle) => area + overlapArea(chipBox(seat.x, seat.y), box),
							0
						),
					0
				);
				// Rings repeat every `step`, so the lean is how far the nearest chip sits from the top.
				const drift = (((offset + Math.PI / 2) % step) + step) % step;
				const lean = Math.min(drift, step - drift);
				if (best === null || cost < best.cost || (cost === best.cost && lean < best.lean)) {
					best = { seats: candidate, cost, lean };
				}
			}

			if (best !== null && best.cost === 0) {
				break;
			}
		}

		const chosen = best?.seats ?? [];
		seats.push(...chosen);
		taken.push(...chosen.map((seat: RingSeat) => chipBox(seat.x, seat.y)));
	});

	return { seats, overflow };
};
